import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'react-toastify';
import type { FacebookUserWhitelistEntry, MetaDmAccessMode } from '@workspace/types';
import { facebookAdminApi } from '../api/facebookApi';

const DEFAULT_REJECTION_MESSAGE_PLACEHOLDER =
  'No tienes permiso para usar este bot. Contacta al administrador.';

type FacebookUserWhitelistSectionProps = {
  channelId: string;
  accessMode: MetaDmAccessMode;
  botLabel?: string;
  whitelistRejectionEnabled?: boolean;
  whitelistRejectionMessage?: string;
  onRejectionChange?: (enabled: boolean, message: string) => void;
  showRejectionSettings?: boolean;
  showSaveReminder?: boolean;
  compact?: boolean;
};

export function FacebookUserWhitelistSection({
  channelId,
  accessMode,
  botLabel,
  whitelistRejectionEnabled = false,
  whitelistRejectionMessage = '',
  onRejectionChange,
  showRejectionSettings = false,
  showSaveReminder = false,
  compact = false,
}: FacebookUserWhitelistSectionProps) {
  const [whitelist, setWhitelist] = useState<FacebookUserWhitelistEntry[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [newPsid, setNewPsid] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const users = await facebookAdminApi.listWhitelist(channelId);
      setWhitelist(users);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  }, [channelId]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleAddUser = async () => {
    if (!newPsid.trim()) {
      return;
    }
    try {
      await facebookAdminApi.addUser(channelId, newPsid.trim(), newLabel.trim() || undefined);
      setNewPsid('');
      setNewLabel('');
      await loadUsers();
      toast.success('Usuario agregado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al agregar usuario');
    }
  };

  const handleRemoveUser = async (psid: string) => {
    try {
      await facebookAdminApi.removeUser(channelId, psid);
      setWhitelist(current => current.filter(entry => entry.psid !== psid));
      toast.success('Usuario eliminado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al eliminar usuario');
    }
  };

  const isRestricted = accessMode === 'WHITELIST_USERS';

  const content = (
    <div className='space-y-4'>
      {!isRestricted && (
        <p className='text-sm rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-amber-900 dark:text-amber-100'>
          El canal está en modo <strong>Abierto</strong>. Para aplicar esta lista, cambiá el modo a{' '}
          <strong>Whitelist de usuarios</strong> en{' '}
          <Link to='/admin/facebook' className='underline font-medium'>
            Administración → Facebook
          </Link>{' '}
          y guardá los cambios.
        </p>
      )}

      {showRejectionSettings && isRestricted && onRejectionChange && (
        <div className='rounded-lg border p-3 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex-1'>
              <Label className='font-medium'>Notificar usuarios no autorizados</Label>
              <p className='text-xs text-muted-foreground mt-1'>
                Si está activo, quien escriba sin estar en la whitelist recibe el mensaje de rechazo.
              </p>
            </div>
            <Switch
              checked={whitelistRejectionEnabled}
              onCheckedChange={checked =>
                onRejectionChange(checked, whitelistRejectionMessage)
              }
            />
          </div>
          {whitelistRejectionEnabled && (
            <div>
              <Label className='text-xs'>Mensaje de rechazo (máx. 1024 caracteres)</Label>
              <Textarea
                value={whitelistRejectionMessage}
                onChange={e =>
                  onRejectionChange(whitelistRejectionEnabled, e.target.value.slice(0, 1024))
                }
                placeholder={DEFAULT_REJECTION_MESSAGE_PLACEHOLDER}
                rows={3}
              />
            </div>
          )}
          {showSaveReminder && (
            <p className='text-xs text-muted-foreground'>
              Guardá los cambios del canal para aplicar la configuración de rechazo.
            </p>
          )}
        </div>
      )}

      <div className='grid gap-2 md:grid-cols-2'>
        <div>
          <Label className='text-xs'>PSID (Page-Scoped ID)</Label>
          <Input
            value={newPsid}
            onChange={e => setNewPsid(e.target.value)}
            placeholder='1234567890123456'
          />
        </div>
        <div>
          <Label className='text-xs'>Etiqueta (opcional)</Label>
          <Input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder='Juan – RRHH'
          />
        </div>
      </div>
      <div className='flex justify-end'>
        <Button type='button' onClick={() => void handleAddUser()} disabled={!newPsid.trim()}>
          Agregar usuario
        </Button>
      </div>

      {loadingUsers ? (
        <p className='text-sm text-muted-foreground'>Cargando usuarios…</p>
      ) : whitelist.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          {isRestricted
            ? 'Aún no hay usuarios cargados. Mientras esté en modo Whitelist, el bot rechaza todos los mensajes.'
            : 'No hay usuarios cargados.'}
        </p>
      ) : (
        <div className='space-y-2'>
          {whitelist.map(entry => (
            <div
              key={entry.id}
              className='flex items-center justify-between border rounded-lg p-2 gap-2'
            >
              <div className='flex-1 min-w-0'>
                <div className='font-mono text-sm'>{entry.psid}</div>
                {entry.label && (
                  <div className='text-xs text-muted-foreground truncate'>{entry.label}</div>
                )}
              </div>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => void handleRemoveUser(entry.psid)}
                title='Eliminar'
              >
                <Trash2 className='h-4 w-4 text-red-500' />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (compact) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Whitelist de usuarios{botLabel ? ` — ${botLabel}` : ''}</CardTitle>
        <CardDescription>
          PSIDs autorizados para escribir por Messenger (DM 1:1).
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
